import { useCallback, useMemo, useState } from 'react';
import { CoreDocumentsStore } from '../../stores/documents.store';
import {
    BatchUploadAppType,
    BatchUploadMetadata,
    BatchUploadStats,
    DEFAULT_BATCH_UPLOAD_CONCURRENCY,
    FileUploadItem,
} from './types';
import { computeUploadStats, createFileUploadItems, getUploadErrorMessage } from './upload-utils';

export interface UseBatchUploadOptions {
    appType: BatchUploadAppType;
    maxConcurrent?: number;
    onComplete?: (results: FileUploadItem[]) => void;
}

export interface UseBatchUploadReturn {
    items: FileUploadItem[];
    isUploading: boolean;
    stats: BatchUploadStats;
    startUpload: (files: File[], metadata: BatchUploadMetadata) => Promise<void>;
    reset: () => void;
}

const runWithConcurrency = async <T>(
    items: T[],
    concurrency: number,
    processor: (item: T) => Promise<void>,
): Promise<void> => {
    if (items.length === 0) {
        return;
    }

    let nextIndex = 0;

    const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
        while (nextIndex < items.length) {
            const currentIndex = nextIndex;
            nextIndex += 1;
            const item = items[currentIndex];

            if (!item) {
                return;
            }

            await processor(item);
        }
    });

    await Promise.all(workers);
};

export const useBatchUpload = ({
    appType,
    maxConcurrent = DEFAULT_BATCH_UPLOAD_CONCURRENCY,
    onComplete,
}: UseBatchUploadOptions): UseBatchUploadReturn => {
    const [items, setItems] = useState<FileUploadItem[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    const documentsStore = useMemo(
        () => new CoreDocumentsStore({ supportGrant: appType === 'support' }),
        [appType],
    );

    const reset = useCallback(() => {
        setItems([]);
        setIsUploading(false);
    }, []);

    const startUpload = useCallback(
        async (files: File[], metadata: BatchUploadMetadata): Promise<void> => {
            if (files.length === 0) {
                return;
            }

            let currentItems = createFileUploadItems(files);
            setItems(currentItems);
            setIsUploading(true);

            const applyItemUpdate = (id: string, patch: Partial<FileUploadItem>): void => {
                currentItems = currentItems.map(item => (item.id === id ? { ...item, ...patch } : item));
                setItems(currentItems);
            };

            const uploadItem = async (item: FileUploadItem): Promise<void> => {
                applyItemUpdate(item.id, { status: 'uploading', errorMessage: undefined });

                try {
                    const documentId = await documentsStore.uploadDocumentFile(item.file, {
                        documentType: metadata.documentType,
                        description: metadata.description,
                        claimId: metadata.claimId,
                    });

                    applyItemUpdate(item.id, {
                        status: 'success',
                        documentId,
                    });
                } catch (error) {
                    applyItemUpdate(item.id, {
                        status: 'failed',
                        errorMessage: getUploadErrorMessage(error),
                    });
                }
            };

            try {
                await runWithConcurrency(currentItems, maxConcurrent, uploadItem);
            } finally {
                setIsUploading(false);
                onComplete?.(currentItems);
            }
        },
        [documentsStore, maxConcurrent, onComplete],
    );

    const stats = useMemo(() => computeUploadStats(items), [items]);

    return {
        items,
        isUploading,
        stats,
        startUpload,
        reset,
    };
};
